import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Container, useTheme, createTheme, ThemeProvider, styled } from '@mui/material';
import {
  Public as GlobeIcon,
  CreditCard as CreditCardIcon,
  Dashboard as Blocks,
  AccountBalanceWallet as WalletIcon,
  Cached as RefreshCcwIcon,
  QrCode as QrCodeIcon,
  Store as StoreIcon,
  Build as Terminal,
  Send as SendIcon,
  Smartphone as PhoneIcon
} from '@mui/icons-material';

// 1. Custom Theme for consistent styling
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#2563eb', // Custom Blue
    },
    background: {
      default: '#f9f9f9', // Light background
    }
  },
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
  },
  shape: {
    borderRadius: 8,
  }
});

// 2. Define the Product Data
const productFeatures = [
  { icon: PhoneIcon, title: 'Split Payments', description: 'Route funds to vendors, service providers, and platform fees in one transaction.' },
  { icon: GlobeIcon, title: 'International Payments', description: 'Accept payments in major global currencies with automatic conversion.' },
  { icon: CreditCardIcon, title: 'Save Cards', description: 'Card details are never stored directly—Zutopay uses PCI-DSS compliant tokenization.' },
  { icon: Blocks, title: 'Payment Modes', description: 'Enable businesses to accept, manage, and disburse payments.' },
  { icon: WalletIcon, title: 'Wallet', description: 'Enable seamless transactions using wallet balance—no bank delays.' },
  { icon: RefreshCcwIcon, title: 'Refunds', description: 'Refund the entire amount or just a portion, based on your business logic.' },
  { icon: QrCodeIcon, title: 'UPI QR\'s', description: 'Customers can scan and pay using any UPI-enabled app like PhonePe, Google Pay, Paytm, or BHIM.' },
  { icon: StoreIcon, title: 'Store QR', description: 'Generate a QR code linked to your store or merchant ID in seconds.' },
  { icon: Terminal, title: 'POS Terminals', description: 'Sleek, portable devices with touchscreen interfaces and built-in printers.' },
  { icon: SendIcon, title: 'Payouts', description: 'Send one-time payments to bank accounts, UPI handles, or wallets.' },
];

// 3. Custom Styled Component for the Card with Border
const StyledCard = styled(Card)(({ theme, highlight }) => ({
  border: '1px solid #e0e0e0',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: 'none',
  height: '100%',
  display: 'flex',
  alignItems: 'stretch',
  transition: 'border-color 0.3s',
  '&:hover': {
    borderColor: '#2563eb',
  },
  // Blue border/highlight on the left for the first card
  ...(highlight && {
    borderLeft: '4px solid #2563eb',
    borderTop: '1px solid #2563eb', // Make the top border match on mobile
    [theme.breakpoints.up('sm')]: {
      borderLeft: '4px solid #2563eb',
      borderTop: '1px solid #e0e0e0', // Reset top border on desktop
    }
  }),
}));

// 4. Product Card Component (Internal Helper Component)
const ProductCard = ({ icon: Icon, title, description, highlight }) => (
  // Use a local useTheme hook to access theme variables if needed within this functional component
  <StyledCard highlight={highlight}>
    <CardContent sx={{ p: 3, width: '100%' }}>
      <Box sx={{ color: 'primary.main', mb: 1 }}>
        <Icon sx={{ fontSize: 28 }} />
      </Box>
      <Typography variant="subtitle1" component="h3" fontWeight="bold" sx={{ color: '#333', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#666' }}>
        {description}
      </Typography>
    </CardContent>
  </StyledCard>
);

// 5. Main App Component (Single component requested by user)
const OurProducts = () => {
  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          {/* Header Section */}
          <Box mb={6} textAlign="left">
            <Typography variant="h4" component="h1"  sx={{ mb: 2, color: '#333' , fontWeight:500 }}>
              Our Products
            </Typography>
            <Typography variant="body1" sx={{ color: '#555', maxWidth: '800px', lineHeight: 1.6 }}>
              Zutopay is a developer-first payment platform designed to simplify global transactions. With a powerful suite of APIs, Zutopay enables businesses to accept payments, issue payouts, and manage invoices with ease. Our infrastructure supports multiple payment methods including UPI, cards, and bank transfers, ensuring seamless experiences across regions. Developers can integrate Zutopay in minutes using our SDKs and sandbox environment.
            </Typography>
          </Box>

          {/* Products Grid */}
          <Grid container spacing={3}>
            {productFeatures.map((feature, index) => (
              // Corrected responsive props from 'size' to 'xs', 'sm', 'md'
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <ProductCard {...feature} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default OurProducts;